import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SeasonTime {
  id: string;           // compound: {cropTypeId}_{regionId}
  cropTypeId: string;
  regionId: string;
  startMonth: number;   // 1–12
  startDay: number;     // 1–31
  endMonth: number;     // 1–12
  endDay: number;       // 1–31
  isDeleted: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

export interface CreateSeasonTimeData {
  cropTypeId: string;
  regionId: string;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
}

export interface UpdateSeasonTimeData {
  startMonth?: number;
  startDay?: number;
  endMonth?: number;
  endDay?: number;
}

export interface SeasonTimeServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToSeasonTime(id: string, data: DocumentData): SeasonTime {
  return {
    id,
    cropTypeId: data.cropTypeId as string,
    regionId: data.regionId as string,
    startMonth: data.startMonth as number,
    startDay: data.startDay as number,
    endMonth: data.endMonth as number,
    endDay: data.endDay as number,
    isDeleted: data.isDeleted as boolean,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    deletedAt: data.deletedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): SeasonTimeServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

/** Compound document ID that enforces one season time per crop+region pair. */
function compoundId(cropTypeId: string, regionId: string): string {
  return `${cropTypeId}_${regionId}`;
}

// ─── Service functions ────────────────────────────────────────────────────────

/** Returns all non-deleted season times in an org, ordered by cropTypeId then regionId. */
export async function getAllSeasonTimes(orgId: string): Promise<SeasonTime[]> {
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'seasonTimes'),
      where('isDeleted', '==', false),
      orderBy('cropTypeId', 'asc'),
      orderBy('regionId', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToSeasonTime(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns a single season time by its compound ID, or null if not found. */
export async function getSeasonTimeById(orgId: string, id: string): Promise<SeasonTime | null> {
  try {
    const snapshot = await getDoc(doc(db, 'orgs', orgId, 'seasonTimes', id));
    if (!snapshot.exists()) return null;
    return docToSeasonTime(snapshot.id, snapshot.data());
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Creates a new season time in an org.
 * - Document ID is `{cropTypeId}_{regionId}` — enforces one season time per pair.
 * - We check explicitly to give a clear error message on duplicate.
 */
export async function createSeasonTime(orgId: string, data: CreateSeasonTimeData): Promise<SeasonTime> {
  try {
    const id = compoundId(data.cropTypeId, data.regionId);
    const existing = await getDoc(doc(db, 'orgs', orgId, 'seasonTimes', id));
    if (existing.exists() && !(existing.data() as DocumentData).isDeleted) {
      throw {
        code: 'already-exists',
        message: 'A season time for this crop type + region combination already exists.',
      };
    }

    await setDoc(doc(db, 'orgs', orgId, 'seasonTimes', id), {
      cropTypeId: data.cropTypeId,
      regionId: data.regionId,
      startMonth: data.startMonth,
      startDay: data.startDay,
      endMonth: data.endMonth,
      endDay: data.endDay,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const snapshot = await getDoc(doc(db, 'orgs', orgId, 'seasonTimes', id));
    return docToSeasonTime(snapshot.id, snapshot.data()!);
  } catch (error) {
    if ((error as SeasonTimeServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/**
 * Updates the date range of an existing season time.
 * cropTypeId and regionId are immutable after creation.
 */
export async function updateSeasonTime(orgId: string, id: string, data: UpdateSeasonTimeData): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'seasonTimes', id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if ((error as SeasonTimeServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Soft-deletes a season time (sets isDeleted + deletedAt). */
export async function deleteSeasonTime(orgId: string, id: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'seasonTimes', id), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Subscribes to real-time updates for all active season times in an org.
 * Returns an unsubscribe function to clean up the listener.
 */
export function subscribeToSeasonTimes(
  orgId: string,
  onData: (_seasonTimes: SeasonTime[]) => void,
  onError: (_error: SeasonTimeServiceError) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'orgs', orgId, 'seasonTimes'),
    where('isDeleted', '==', false),
    orderBy('cropTypeId', 'asc'),
    orderBy('regionId', 'asc'),
  );
  return onSnapshot(
    q,
    snapshot => onData(snapshot.docs.map(d => docToSeasonTime(d.id, d.data()))),
    err => onError(toServiceError(err)),
  );
}
